  /**
 * Flow draft utilities
 * For Media Devoted | Created By Ciro Autiero
 *
 * Keeps a local, unsaved “draft” version of a flow for UI editing.
 */

export interface DraftWeightedItem {
  id: string;
  name: string;
  weight: number;
  originalWeight: number;
}

export interface DraftPath {
  id: string;
  name: string;
  groupId?: string;
  weight: number;
  active: boolean;
  originalActive: boolean;
  originalWeight: number;
  landers: DraftWeightedItem[];
  offers: DraftWeightedItem[];
}

export function buildDraftPaths(params: {
  paths: Array<{
    id?: string;
    name?: string;
    groupId?: string;
    weight: number;
    active?: boolean;
    landers?: Array<{ id: string; name: string; weight: number }>;
    offers?: Array<{ id: string; name: string; weight: number }>;
  }>;
}): DraftPath[] {
  return params.paths
    .filter((p): p is (typeof params.paths)[number] & { id: string } => Boolean(p?.id))
    .map((p) => {
      const active = p.active !== false;
      const landers = (p.landers ?? []).map((l) => ({
        id: l.id,
        name: l.name,
        weight: l.weight,
        originalWeight: l.weight,
      }));
      const offers = (p.offers ?? []).map((o) => ({
        id: o.id,
        name: o.name,
        weight: o.weight,
        originalWeight: o.weight,
      }));

      return {
        id: String(p.id),
        name: String(p.name ?? 'Unnamed Path'),
        groupId: p.groupId ? String(p.groupId) : undefined,
        weight: p.weight,
        originalWeight: p.weight,
        active,
        originalActive: active,
        landers,
        offers,
      };
    });
}

export function isPathDirty(path: DraftPath): boolean {
  if (path.active !== path.originalActive) return true;
  if (path.weight !== path.originalWeight) return true;
  if (path.landers.some((l) => l.weight !== l.originalWeight)) return true;
  if (path.offers.some((o) => o.weight !== o.originalWeight)) return true;
  return false;
}

export function countDirty(params: { paths: DraftPath[] }): number {
  return params.paths.reduce((acc, p) => acc + (isPathDirty(p) ? 1 : 0), 0);
}

export function togglePathDraft(params: { path: DraftPath; nextActive: boolean }): DraftPath {
  if (params.nextActive) {
    const restoredWeight = params.path.weight > 0 ? params.path.weight : Math.max(1, params.path.originalWeight);
    return { ...params.path, active: true, weight: restoredWeight };
  }
  return { ...params.path, active: false, weight: 0 };
}

export function toggleWeightedItemDraft(params: {
  item: DraftWeightedItem;
  nextActive: boolean;
}): DraftWeightedItem {
  if (params.nextActive) {
    const restored = params.item.originalWeight > 0 ? params.item.originalWeight : 1;
    return { ...params.item, weight: restored };
  }
  return { ...params.item, weight: 0 };
}

/** The draft after a successful save: today's values become the originals. */
export function markDraftSaved(paths: DraftPath[]): DraftPath[] {
  return paths.map((p) => ({
    ...p,
    originalActive: p.active,
    originalWeight: p.weight,
    landers: p.landers.map((l) => ({ ...l, originalWeight: l.weight })),
    offers: p.offers.map((o) => ({ ...o, originalWeight: o.weight })),
  }));
}
